import React, { useEffect, useState } from "react";
import { TaleUpload } from "@components/TaleUpload";
import { TaleInput } from "@components/TaleInput";
import { Button, ClickAwayListener } from "@mui/material";
import TaleButton from "@components/TaleButton";
import { companyService} from "@/api/companies";
import {assetsService} from "@/api/assets";

const AddCompanyPopup = ({ onClose, onValidate, isEmbedded = false, existingCompany = null }) => {
    const isEditMode = !!existingCompany;

    const [selectedFiles, setSelectedFiles] = useState([]);

    const [formData, setFormData] = useState({
        companyName: "",
        industry: "",
        numberOfEmployees: "",
        address: "",
        website: "",
        logo: null,
        // Create-only fields
        firstname: "",
        lastname: "",
        email: "",
        phoneNumber: "",
        password: "",
        confirmPassword: "",
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [logoUploading, setLogoUploading] = useState(false);
    const [logoPreviewUrl, setLogoPreviewUrl] = useState(null);

    // Populate form when editing
    useEffect(() => {
        if (existingCompany) {
            setFormData((prev) => ({
                ...prev,
                companyName: existingCompany.companyName || "",
                industry: existingCompany.industry || "",
                numberOfEmployees: existingCompany.numberOfEmployees || "",
                address: existingCompany.address || "",
                website: existingCompany.website || "",
            }));
        }
    }, [existingCompany]);

    useEffect(() => {
        return () => {
            if (logoPreviewUrl) {
                URL.revokeObjectURL(logoPreviewUrl);
            }
        };
    }, [logoPreviewUrl]);

    useEffect(() => {
        if (isEditMode && existingCompany?.assets) {
            const logoAsset = existingCompany.assets.find(a => a.description === "COMPANY_LOGO");
            if (logoAsset?.url) {
                setLogoPreviewUrl(logoAsset.url);
                setSelectedFiles([logoAsset.url]);
                setFormData(prev => ({ ...prev, logo: logoAsset }));
            }
        }
    }, [isEditMode, existingCompany]);

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Clean up old preview URL
        if (logoPreviewUrl) {
            URL.revokeObjectURL(logoPreviewUrl);
        }

        // Create preview URL
        const newPreviewUrl = URL.createObjectURL(file);
        setLogoPreviewUrl(newPreviewUrl);
        setSelectedFiles([file]);

        if (isEditMode && existingCompany?.id) {
            // Edit mode: upload immediately
            setLogoUploading(true);
            try {
                // Delete old logo if exists
                if (existingCompany.assets) {
                    const oldLogo = existingCompany.assets.find(a => a.description === "COMPANY_LOGO");
                    if (oldLogo?.id) {
                        await assetsService.deleteAsset(oldLogo.id);
                    }
                }
                // Upload new logo
                const newLogo = await assetsService.uploadAsset({
                    file,
                    contentType: "IMAGE",
                    description: "COMPANY_LOGO",
                    entityId: existingCompany.id,
                    entityType: "COMPANY",
                });
                setFormData((prev) => ({ ...prev, logo: newLogo }));
            } catch (err) {
                console.error("Failed to upload logo:", err);
                setError("Failed to upload logo");
            } finally {
                setLogoUploading(false);
            }
        } else {
            // Create mode: store file temporarily
            setFormData((prev) => ({ ...prev, logo: file }));
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleValidate = async () => {
        if (disabled) return;
        setError(null);

        if (!isEditMode) {
            if (formData.password.length < 6) {
                setError("Le mot de passe doit contenir au moins 6 caractères.");
                return;
            }
            if (formData.password !== formData.confirmPassword) {
                setError("Les mots de passe ne correspondent pas.");
                return;
            }
        }

        try {
            setLoading(true);
            let result;

            if (isEditMode) {
                const updatePayload = {
                    id: existingCompany.id,
                    companyName: formData.companyName,
                    industry: formData.industry,
                    numberOfEmployees: formData.numberOfEmployees
                        ? parseInt(formData.numberOfEmployees, 10)
                        : null,
                    address: formData.address,
                    website: formData.website,
                };
                result = await companyService.updateCompany(updatePayload);
                // If logo was uploaded separately (already handled in handleFileChange), ensure result has updated assets
                if (formData.logo && formData.logo.url) {
                    result.assets = result.assets || [];
                    result.assets.push(formData.logo);
                }
            } else {
                result = await companyService.createCompany({
                    companyName: formData.companyName,
                    industry: formData.industry,
                    numberOfEmployees: formData.numberOfEmployees
                        ? parseInt(formData.numberOfEmployees, 10)
                        : null,
                    address: formData.address,
                    website: formData.website,
                    firstname: formData.firstname,
                    lastname: formData.lastname,
                    email: formData.email,
                    phoneNumber: formData.phoneNumber || null,
                    password: formData.password,
                });

                // Upload logo after company creation if a file was selected
                if (formData.logo && formData.logo instanceof File) {
                    setLogoUploading(true);
                    try {
                        const uploadedLogo = await assetsService.uploadAsset({
                            file: formData.logo,
                            contentType: "IMAGE",
                            description: "COMPANY_LOGO",
                            entityId: result.id,
                            entityType: "COMPANY",
                        });
                        result.logo = uploadedLogo;
                        result.assets = [uploadedLogo];
                    } catch (err) {
                        console.error("Failed to upload logo after creation:", err);
                    } finally {
                        setLogoUploading(false);
                    }
                }
            }

            onValidate(result);
            onClose();
        } catch (err) {
            console.error("Error saving company:", err.message);
            setError(err?.response?.data?.message || "Une erreur est survenue.");
        } finally {
            setLoading(false);
        }
    };

    // Required fields differ by mode
    const disabled = isEditMode
        ? !formData.companyName || !formData.industry
        : !formData.companyName ||
        !formData.industry ||
        !formData.firstname ||
        !formData.lastname ||
        !formData.email ||
        !formData.password;

    return (
        <ClickAwayListener onClickAway={onClose}>
            <div
                style={
                    isEmbedded
                        ? {}
                        : {
                            padding: 20,
                            borderRadius: 8,
                            boxShadow: "0 0 10px rgba(0, 0, 0, 0.3)",
                        }
                }
                className="flex flex-col !gap-5 bg-white w-full md:w-[480px]"
            >
                {!isEmbedded && (
                    <div
                        style={{
                            textAlign: "justify",
                            color: "black",
                            fontSize: 24,
                            fontFamily: "revert",
                            fontWeight: "700",
                            wordWrap: "break-word",
                        }}
                    >
                        {isEditMode ? "Edit company" : "Add new company"}
                    </div>
                )}

                <div className={`${isEmbedded ? "" : "overflow-y-scroll !max-h-[700px]"} -z-0`}>
                    {/* Logo upload — available in both modes */}
                    <TaleUpload
                        title={"Upload logo (Optional)"}
                        extensions={".jpg .png .jpeg"}
                        onChange={handleFileChange}
                        files={selectedFiles}
                        removeFile={async () => {
                            if (isEditMode && existingCompany?.id) {
                                setLogoUploading(true);
                                try {
                                    const oldLogo = existingCompany.assets?.find(a => a.description === "COMPANY_LOGO");
                                    if (oldLogo?.id) {
                                        await assetsService.deleteAsset(oldLogo.id);
                                    }
                                    setFormData((prev) => ({ ...prev, logo: null }));
                                    // Also remove from existingCompany reference
                                    if (existingCompany.assets) {
                                        existingCompany.assets = existingCompany.assets.filter(a => a.description !== "COMPANY_LOGO");
                                    }
                                } catch (err) {
                                    console.error("Failed to delete logo:", err);
                                } finally {
                                    setLogoUploading(false);
                                }
                            } else {
                                setSelectedFiles([]);
                                setFormData((prev) => ({ ...prev, logo: null }));
                            }
                            if (logoPreviewUrl) {
                                URL.revokeObjectURL(logoPreviewUrl);
                                setLogoPreviewUrl(null);
                            }
                        }}
                        singleFile={true}
                        inputProps={{ accept: "image/*" }}
                        loading={logoUploading}
                    />

                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        {/* ── Company fields (both modes) ── */}
                        <TaleInput
                            label="Company name"
                            type="text"
                            name="companyName"
                            value={formData.companyName}
                            placeholder="Enter company name"
                            onChange={handleInputChange}
                        />

                        <TaleInput
                            label="Industry"
                            name="industry"
                            value={formData.industry}
                            onChange={handleInputChange}
                        >
                        </TaleInput>

                        <TaleInput
                            label="Number of Employees"
                            type="number"
                            name="numberOfEmployees"
                            value={formData.numberOfEmployees}
                            onChange={handleInputChange}
                        />

                        <TaleInput
                            label="Address"
                            type="text"
                            name="address"
                            value={formData.address}
                            onChange={handleInputChange}
                        />

                        <TaleInput
                            label="Website"
                            type="url"
                            name="website"
                            value={formData.website}
                            onChange={handleInputChange}
                        />

                        {/* ── Admin account fields (create mode only) ── */}
                        {!isEditMode && (
                            <>
                                <div
                                    style={{
                                        color: "#666",
                                        fontSize: 12,
                                        fontWeight: 600,
                                        textTransform: "uppercase",
                                        letterSpacing: "0.05em",
                                        marginTop: 8,
                                    }}
                                >
                                    Administrator account
                                </div>

                                <div style={{ display: "flex", gap: 8 }}>
                                    <TaleInput
                                        label="First name"
                                        type="text"
                                        name="firstname"
                                        value={formData.firstname}
                                        placeholder="First name"
                                        onChange={handleInputChange}
                                    />
                                    <TaleInput
                                        label="Last name"
                                        type="text"
                                        name="lastname"
                                        value={formData.lastname}
                                        placeholder="Last name"
                                        onChange={handleInputChange}
                                    />
                                </div>

                                <TaleInput
                                    label="Email"
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    placeholder="admin@company.com"
                                    onChange={handleInputChange}
                                />

                                <TaleInput
                                    label="Phone number (optional)"
                                    type="tel"
                                    name="phoneNumber"
                                    value={formData.phoneNumber}
                                    onChange={handleInputChange}
                                />

                                <TaleInput
                                    label="Password"
                                    type="password"
                                    name="password"
                                    value={formData.password}
                                    placeholder="Min. 6 characters"
                                    onChange={handleInputChange}
                                />

                                <TaleInput
                                    label="Confirm password"
                                    type="password"
                                    name="confirmPassword"
                                    value={formData.confirmPassword}
                                    placeholder="Repeat password"
                                    onChange={handleInputChange}
                                />
                            </>
                        )}

                        {error && (
                            <span style={{ color: "#dc2626", fontSize: 13, textAlign: "center" }}>
                {error}
              </span>
                        )}
                    </div>
                </div>

                <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <Button
                        variant="outlined"
                        onClick={onClose}
                        className="!font-semibold !border !border-[#111] !text-[#111] basis-1/3 !rounded-2xl !normal-case"
                    >
                        Cancel
                    </Button>
                    <TaleButton
                        role="business"
                        onClick={handleValidate}
                        disabled={disabled}
                        className={`!rounded-2xl !font-semibold basis-1/3 ${disabled ? "!text-[#666]" : ""}`}
                        loading={loading}
                        loadingCircleSize={25}
                    >
                        {isEditMode ? "Save" : "Submit"}
                    </TaleButton>
                </div>
            </div>
        </ClickAwayListener>
    );
};

export default AddCompanyPopup;